import { filter, groupBy, map, get, set, mapValues, uniqBy, compact, forEach } from 'lodash';

import { CUSTOM_COLLECTION_ITEM_TYPES } from 'constants/collection';
import actions from 'state/constants';
import initialState from 'state/initialState';
import Settings from 'utils/Settings';

const setCustomCollectionDataReducer = (state, action) => ({
  ...state,
  customCollection: {
    ...action.payload.data,
  },
});

const fetchCustomCollectionRequestReducer = state => ({
  ...state,
  customCollection: { ...state.customCollection, isLoading: true },
});

const getMasterProducts = ({ collection, colorwayItems }) => {
  // 1 - Create master product groups
  // Create an array of product handles
  const productHandles = compact(
    map(colorwayItems, ({ type, id }) =>
      type === CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY ? id : null
    )
  );

  // Look up each product in state
  const products = compact(map(productHandles, handle => get(collection, ['products', handle])));

  const groupedProducts = groupBy(products, 'master');

  const masterProducts = mapValues(groupedProducts, productGroup => ({
    currentProduct: get(productGroup, [0, 'handle']),
    products: map(productGroup, 'handle'),
  }));

  // 2 - Create ordered list of master product and story card items
  const masterProductItems = uniqBy(
    map(colorwayItems, item => {
      const { type, id, data } = item;

      if (type === CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY) {
        return {
          type: CUSTOM_COLLECTION_ITEM_TYPES.MASTER_PRODUCT,
          id: get(collection, ['products', id, 'master']),
        };
      }

      return { type, id, data };
    }),
    ({ type, id }) => `${type}--${id}`
  );

  return {
    masterProducts,
    masterProductItems,
  };
};

const normalizeCustomCollection = ({ colorwayItems, collection }) => {
  let normalizedColorwayItems = colorwayItems;

  // 0 - If data metafield is missing, fallback gracefully using legacy field
  if (!normalizedColorwayItems) {
    normalizedColorwayItems = map(collection.productsSortOrder, handle => ({
      type: CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY,
      id: handle,
    }));
  }

  // 1 - Crunch Colorways
  // Filter items that are not published
  normalizedColorwayItems = filter(normalizedColorwayItems, ({ type, id }) => {
    if (type === CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY) {
      return !!get(collection, ['products', id]);
    }

    return true;
  });

  // 2 - Crunch Storycards
  const storyCards = {};

  // move data from within the nested story card to the story card map
  normalizedColorwayItems = map(normalizedColorwayItems, item => {
    const { type, id, data } = item;

    const colorwayItem = {
      type,
      id,
    };

    if (item.type === CUSTOM_COLLECTION_ITEM_TYPES.STORY_CARD) {
      set(storyCards, [id], data);
      colorwayItem.data = data;
    }

    // localize and leave data for basic components
    if (item.type === CUSTOM_COLLECTION_ITEM_TYPES.SUB_HEADER) {
      const localizedData = get(data, Settings.currentLocale);
      colorwayItem.data = localizedData;
    }

    return colorwayItem;
  });

  // 3 - Crunch Master Products

  const { masterProducts, masterProductItems } = getMasterProducts({
    collection,
    colorwayItems: normalizedColorwayItems,
  });

  return {
    colorwayItems: normalizedColorwayItems,
    masterProductItems,
    masterProducts,
    storyCards,
  };
};

const fetchCustomCollectionSuccessReducer = (state, action) => {
  const { masterProducts, colorwayItems, masterProductItems, storyCards } =
    normalizeCustomCollection({
      colorwayItems: state.customCollection.colorwayItems,
      collection: action.payload.collection,
    });

  return {
    ...state,
    customCollection: {
      ...state.customCollection,
      colorwayItems,
      masterProductItems,
      isLoading: false,
    },
    products: { ...state.products, ...action.payload.collection.products },
    masterProducts,
    storyCards,
  };
};

const setMasterProductCardProduct = (state, action) => {
  const { masterHandle, productHandle } = action.payload;

  return {
    ...state,
    masterProducts: {
      ...state.masterProducts,
      [masterHandle]: {
        ...state.masterProducts[masterHandle],
        currentProduct: productHandle,
      },
    },
  };
};

const setMasterProductCardCarouselIndex = (state, action) => {
  const { masterHandle, carouselIndex } = action.payload;

  return {
    ...state,
    masterProducts: {
      ...state.masterProducts,
      [masterHandle]: {
        ...state.masterProducts[masterHandle],
        carouselIndex,
      },
    },
  };
};

const resetAllMasterProductCardCarouselIndices = state => {
  const newState = {
    ...state,
  };

  forEach(newState.masterProducts, (masterProduct, masterProductHandle) => {
    set(newState.masterProducts, [masterProductHandle, 'carouselIndex'], 0);
  });

  return newState;
};

export const reducer = (state = initialState.collection, action) => {
  switch (action.type) {
    case actions.SET_CUSTOM_COLLECTION_DATA:
      return setCustomCollectionDataReducer(state, action);
    case actions.FETCH_CUSTOM_COLLECTION_REQUEST:
      return fetchCustomCollectionRequestReducer(state, action);
    case actions.FETCH_CUSTOM_COLLECTION_SUCCESS:
      return fetchCustomCollectionSuccessReducer(state, action);
    case actions.SET_MASTER_PRODUCT_CARD_PRODUCT:
      return setMasterProductCardProduct(state, action);
    case actions.SET_MASTER_PRODUCT_CARD_CAROUSEL_INDEX:
      return setMasterProductCardCarouselIndex(state, action);
    case actions.SET_COLLECTION_PAGE_FILTER:
    case actions.CLEAR_COLLECTION_PAGE_FILTERS:
      return resetAllMasterProductCardCarouselIndices(state, action);
    case actions.FETCH_CUSTOM_COLLECTION_FAILURE:
    default:
      return state;
  }
};

export default reducer;
